import React from "react"
import { Dispatch } from "redux"

import { DESC } from "const"
import { QidModelSafetyFunction } from "server/models"
import { sort } from "client/store/utils"
import {
    ResultDatapointListType,
    SilModelDevicesQidExpanded,
    CalculateQidResultProps,
    CalculateQidResultData,
    setSilModelDevicesQidExpanded,
} from "client/store"
import {
    TableRow,
    TableCell,
    TableCellContentWrapper,
    TableCellContent,
    Empty,
    NoDatapoints,
} from "client/components"

import {
    TableCellLined,
    RowCollapseControl,
} from "client/pages/qid/product/models/list/styled"

import { ColumnsType, SelectDeviceType, SelectDevice } from "../columns"
import Datapoint from "../nestedDatapoints"
import OptionRows from "./optionRows"

import { paddingStyle } from "./index"

const SafetyFunctionRows = (
    columns: ColumnsType,
    safetyFunctions: readonly QidModelSafetyFunction[],
    expanded: SilModelDevicesQidExpanded,
    modelId: number,
    lastModelRow: boolean,
    baseCalculationProps: CalculateQidResultProps,
    selectModelProps: SelectDeviceType,
    datapointList: ResultDatapointListType,
    dispatch: Dispatch,
) => {
    const toggleSafetyFunction = (functionId: number) => {
        const expandedFunctions = expanded.safetyFunctionIds.slice()
        if (expandedFunctions.includes(functionId)) {
            dispatch(setSilModelDevicesQidExpanded({
                safetyFunctionIds: expandedFunctions.filter((id) => id !== functionId),
            }))
        } else {
            expandedFunctions.push(functionId)
            dispatch(setSilModelDevicesQidExpanded({ safetyFunctionIds: expandedFunctions }))
        }
    }

    let rows: React.ReactNode[] = []

    if (!safetyFunctions.length) {
        rows.push(
            <TableRow key={`emptyModel_${modelId}`} size="sm">
                <TableCellLined lineThrough={!lastModelRow} />
                <TableCellLined lineHalfCross />
                <TableCell colSpan={100}>
                    <TableCellContentWrapper>
                        <Empty />
                    </TableCellContentWrapper>
                </TableCell>
            </TableRow>,
        )
    }

    safetyFunctions.forEach((safetyFunction, functionIndex) => {
        const functionExpanded = expanded.safetyFunctionIds.includes(safetyFunction.id)
        const lastFunctionRow = safetyFunctions.length - 1 === functionIndex
        const safetyFunctionAvg = CalculateQidResultData({
            ...baseCalculationProps,
            modelId,
            safetyFunctionId: safetyFunction.id,
        })

        const selectFunctionProps: SelectDeviceType = {
            ...selectModelProps,
            safetyFunction,
            dataAvg: safetyFunctionAvg,
        }

        rows.push(
            <TableRow
                key={`safetyFunction_${safetyFunction.id}`}
                size="sm"
                onClick={() => SelectDevice(selectFunctionProps)}
            >
                <TableCellLined lineThrough={!lastModelRow} />
                <TableCellLined
                    // all rows have "cross line", and the last row has only "half line"
                    lineCross={!lastFunctionRow}
                    lineHalfCross={lastFunctionRow}
                />
                <TableCell colSpan={2}>
                    <TableCellContentWrapper style={paddingStyle}>
                        <TableCellContent>
                            <RowCollapseControl
                                collapsed={!functionExpanded}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    toggleSafetyFunction(safetyFunction.id)
                                }}
                            />
                            {safetyFunction.name}
                        </TableCellContent>
                    </TableCellContentWrapper>
                </TableCell>
                {
                    columns.commonColumns.map((column) => (
                        <TableCell
                            key={column.field}
                            cellWidth={column.cellWidth}
                            cellMinWidth={column.cellMinWidth}
                            cellMaxWidth={column.cellMaxWidth}
                        >
                            <TableCellContentWrapper>
                                <TableCellContent>
                                    {
                                        column.formatter
                                            ? column.formatter(safetyFunction)
                                            : safetyFunction[column.field as keyof QidModelSafetyFunction]
                                    }
                                </TableCellContent>
                            </TableCellContentWrapper>
                        </TableCell>
                    ))
                }
                {
                    columns.resultColumns.map((column) => (
                        <TableCell
                            key={column.field}
                            cellWidth={column.cellWidth}
                            cellMinWidth={column.cellMinWidth}
                            cellMaxWidth={column.cellMaxWidth}
                        >
                            <TableCellContentWrapper>
                                <TableCellContent>
                                    {column.formatter(safetyFunctionAvg)}
                                </TableCellContent>
                            </TableCellContentWrapper>
                        </TableCell>
                    ))
                }
            </TableRow>,
        )

        if (functionExpanded) {
            // order datapoints by "createdAt" DESC - show the newest on top
            const functionKey = `model_${modelId}_safetyFunction_${safetyFunction.id}_option_null`
            const functionDatapoints = datapointList[functionKey] && datapointList[functionKey].datapoints
                ? datapointList[functionKey].datapoints.slice().sort((a, b) => sort(a, b, "createdAt", DESC))
                : []

            rows.push(
                <TableRow key={`safetyFunction_${safetyFunction.id}_datapoints`} size="fit">
                    <TableCellLined lineThrough={!lastModelRow} />
                    <TableCellLined lineThrough={!lastFunctionRow} />
                    <TableCellLined lineThrough />
                    <TableCell colSpan={100}>
                        <TableCellContentWrapper columnDirection>
                            {
                                functionDatapoints.length === 0
                                && <NoDatapoints />
                            }
                            {
                                functionDatapoints.map((datapoint) => {
                                    const datapointAvg = CalculateQidResultData({
                                        ...baseCalculationProps,
                                        modelId: safetyFunction.modelId,
                                        safetyFunctionId: safetyFunction.id,
                                        datapointId: datapoint.id,
                                    })

                                    const selectFunctionDatapointProps: SelectDeviceType = {
                                        ...selectFunctionProps,
                                        method: null,
                                        datapoint,
                                        dataAvg: datapointAvg,
                                    }

                                    return (
                                        <Datapoint
                                            key={datapoint.id}
                                            datapoint={datapoint}
                                            datapointAvg={datapointAvg}
                                            onClick={() => SelectDevice(selectFunctionDatapointProps)}
                                        />
                                    )
                                })
                            }
                        </TableCellContentWrapper>
                    </TableCell>
                </TableRow>,
            )

            const optionRows = OptionRows(
                columns,
                safetyFunction.options,
                expanded,
                modelId,
                safetyFunction.id,
                lastModelRow,
                lastFunctionRow,
                baseCalculationProps,
                selectFunctionProps,
                datapointList,
                dispatch,
            )
            rows = rows.concat(optionRows)
        }
    })

    return rows
}
export default SafetyFunctionRows
